import React, { useState, useRef, useEffect } from 'react';
import { BACKEND_URL } from '../utils/backend';

const MODEL_OPTIONS = [
  { value: 'openai', label: '🟢 OpenAI' },
  { value: 'gemini', label: '🔵 Gemini' },
  { value: 'linkai', label: '🟣 LinkAI' },
];

function getInitialModelSource() {
  return localStorage.getItem('model_source') || 'openai';
}

const LINKAI_APPCODE_OPTIONS = [
  { value: 'your_general_appcode', label: 'General-purpose agent' },
  { value: 'iGpUvtoQ', label: 'Fashion buyer agent' },
];
const DEFAULT_LINKAI_APPCODE = 'iGpUvtoQ';

function getInitialLinkaiAppcode() {
  return localStorage.getItem('linkai_appcode') || DEFAULT_LINKAI_APPCODE;
}

// Spinner copied from KnowledgeBase
function Spinner() {
  return (
    <div className="flex justify-center items-center h-32">
      <svg className="animate-spin h-8 w-8 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path>
      </svg>
    </div>
  );
}

export default function Chat({ userId }) {
  const [messages, setMessages] = useState([
    { sender: 'agent', text: 'Hi! I am your fashion buyer assistant. How can I help you today?' }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [modelSource, setModelSource] = useState(getInitialModelSource());
  const [linkaiAppcode, setLinkaiAppcode] = useState(getInitialLinkaiAppcode());
  const [selectedImages, setSelectedImages] = useState([]);
  const chatEndRef = useRef(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const [renderTime, setRenderTime] = useState(null);
  const [showRenderTime, setShowRenderTime] = useState(false);
  const renderStartRef = React.useRef(null);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    localStorage.setItem('model_source', modelSource);
  }, [modelSource]);

  useEffect(() => {
    if (modelSource === 'linkai') {
      localStorage.setItem('linkai_appcode', linkaiAppcode);
    }
  }, [linkaiAppcode, modelSource]);

  useEffect(() => {
    renderStartRef.current = performance.now();
    setInitialLoading(true);
    setTimeout(() => {
      setInitialLoading(false);
      const end = performance.now();
      const seconds = ((end - renderStartRef.current) / 1000).toFixed(2);
      setRenderTime(seconds);
      setShowRenderTime(true);
      setTimeout(() => setShowRenderTime(false), 5000);
    }, 0);
  }, []);

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    setSelectedImages(files);
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!input.trim() && selectedImages.length === 0) return;
    const userMessage = { sender: 'user', text: input, images: selectedImages.map(f => URL.createObjectURL(f)) };
    setMessages((msgs) => [...msgs, userMessage]);
    setInput('');
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('user_id', userId);
      formData.append('message', input);
      formData.append('model_source', modelSource);
      if (modelSource === 'linkai') {
        formData.append('appcode', linkaiAppcode);
      }
      selectedImages.forEach((file, idx) => {
        formData.append('images', file);
      });
      const res = await fetch(`${BACKEND_URL}/chat`, {
        method: 'POST',
        body: formData,
      });
      const data = await res.json();
      setMessages((msgs) => [...msgs, { sender: 'agent', text: data.response || data.reply || 'No response from agent.', images: data.image_urls || [] }]);
      setSelectedImages([]);
    } catch (err) {
      setMessages((msgs) => [...msgs, { sender: 'agent', text: 'Sorry, there was an error contacting the assistant.' }]);
    }
    setLoading(false);
  };

  if (initialLoading) return <Spinner />;
  return (
    <div className="flex flex-col h-full max-h-[80vh] bg-white rounded-lg shadow p-4 animate-fade-in" style={{ position: 'relative' }}>
      <div className="flex items-center mb-4 gap-4">
        <h2 className="text-xl font-bold flex-1">💬 Chat with Your Fashion Buyer Agent</h2>
        <select
          className="border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200"
          value={modelSource}
          onChange={e => setModelSource(e.target.value)}
          aria-label="Select LLM backend"
        >
          {MODEL_OPTIONS.map(opt => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      </div>
      {modelSource === 'linkai' && (
        <div className="mb-2 flex items-center gap-2">
          <label htmlFor="linkaiAppcode" className="text-sm font-medium">Agent Mode:</label>
          <select
            id="linkaiAppcode"
            className="border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200"
            value={linkaiAppcode}
            onChange={e => setLinkaiAppcode(e.target.value)}
            style={{ width: 220 }}
          >
            {LINKAI_APPCODE_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>
      )}
      <div className="flex-1 overflow-y-auto mb-4 space-y-2 px-2" style={{ minHeight: '300px' }}>
        {messages.map((msg, idx) => (
          <div key={idx} className={`flex ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-xs px-4 py-2 rounded-2xl shadow text-sm whitespace-pre-line ${msg.sender === 'user' ? 'bg-blue-500 text-white rounded-br-none' : 'bg-gray-100 text-gray-800 rounded-bl-none'}`}>
              {msg.text}
              {msg.images && msg.images.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {msg.images.map((img, i) => (
                    <img key={i} src={img} alt="uploaded" className="w-24 h-24 object-cover rounded" />
                  ))}
                </div>
              )}
            </div>
          </div>
        ))}
        <div ref={chatEndRef} />
      </div>
      <form onSubmit={sendMessage} className="flex gap-2 items-center">
        <label className="cursor-pointer">
          <span className="text-2xl mr-2">📷</span>
          <input type="file" accept="image/*" multiple style={{ display: 'none' }} onChange={handleImageChange} disabled={loading} />
        </label>
        {selectedImages.length > 0 && (
          <div className="flex gap-1">
            {selectedImages.map((file, idx) => (
              <img key={idx} src={URL.createObjectURL(file)} alt="preview" className="w-10 h-10 object-cover rounded" />
            ))}
          </div>
        )}
        <input
          className="flex-1 border border-gray-300 rounded-full px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-200"
          type="text"
          value={input}
          onChange={e => setInput(e.target.value)}
          placeholder="Type your message..."
          disabled={loading}
        />
        <button
          type="submit"
          className="bg-blue-500 text-white px-6 py-2 rounded-full font-semibold shadow hover:bg-blue-600 disabled:opacity-50"
          disabled={loading || (!input.trim() && selectedImages.length === 0)}
        >
          {loading ? '...' : 'Send'}
        </button>
      </form>
      {showRenderTime && (
        <div style={{ position: 'fixed', bottom: 24, right: 24, zIndex: 50 }} className="bg-gray-800 text-white text-xs px-3 py-1 rounded shadow animate-fade-in">
          Render Time: {renderTime} seconds
        </div>
      )}
    </div>
  );
} 